/**
 * MainTabBar — Plan 05-12 Task 2.
 *
 * Horizontal tab bar for the 3 /main tabs per UI-SPEC §"Tab structure".
 *
 * Active state: text-ink-900 font-semibold + border-b-2 border-brand-600 overlapping
 * the container's ink-200 line (use -mb-px).
 * Inactive: text-ink-500 + hover text-brand-600.
 * Mobile (<sm): overflow-x-auto.
 * URL deep-linking: onChange callback drives ?tab= query param.
 * Keyboard: ← → arrow keys cycle tabs (standard ARIA tablist pattern).
 */

export type TabKey =
  | 'summary'
  | 'sales'
  | 'marketing'
  | 'cs'
  | 'services'
  | 'finance'
  | 'team'
  | 'taxonomy';

/** Legacy ?tab= values still honoured (quick-261104-ia3: ROI became Finance). */
export const LEGACY_TAB_ALIASES: Record<string, TabKey> = { roi: 'finance' };

interface TabConfig {
  key: TabKey;
  label: string;
  tileCount: number;
  /** Summary/Taxonomy are reading tabs, not tile grids — they show no count. */
  hideCount?: boolean;
}

interface MainTabBarProps {
  activeTab: TabKey;
  onChange: (tab: TabKey) => void;
  /** Tile counts for the three data tabs; reading tabs omit their entry. */
  tileCounts: Partial<Record<TabKey, number>>;
}

// Summary is the default landing readout, then the three data tabs, then the
// Taxonomy glossary as the reference at the end. Summary + Taxonomy are reading
// tabs and show no tile count.
const TABS: TabConfig[] = [
  // quick-261104-ia3 (John 2026-11-04): department tabs under the exec band —
  // Sales, Marketing (one tab), CS, Services, Finance. Counts dropped: every
  // tab is a fixed layout now, so a tile count says nothing useful.
  { key: 'summary', label: 'Summary', tileCount: 0, hideCount: true },
  { key: 'sales', label: 'Sales', tileCount: 0, hideCount: true },
  { key: 'marketing', label: 'Marketing', tileCount: 0, hideCount: true },
  { key: 'cs', label: 'Customer Success', tileCount: 0, hideCount: true },
  { key: 'services', label: 'Services', tileCount: 0, hideCount: true },
  { key: 'finance', label: 'Finance', tileCount: 0, hideCount: true },
  { key: 'team', label: 'Team', tileCount: 0, hideCount: true },
  { key: 'taxonomy', label: 'Taxonomy', tileCount: 0, hideCount: true },
];

export function MainTabBar({ activeTab, onChange, tileCounts }: MainTabBarProps): React.JSX.Element {
  const tabs = TABS.map((t) => ({ ...t, tileCount: tileCounts[t.key] ?? 0 }));

  function handleKeyDown(e: React.KeyboardEvent, currentIdx: number): void {
    if (e.key === 'ArrowRight') {
      const next = tabs[(currentIdx + 1) % tabs.length];
      if (next) onChange(next.key);
    } else if (e.key === 'ArrowLeft') {
      const prev = tabs[(currentIdx - 1 + tabs.length) % tabs.length];
      if (prev) onChange(prev.key);
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Dashboard tabs"
      className="flex overflow-x-auto border-b border-ink-200"
    >
      {tabs.map((tab, idx) => {
        const isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.key}`}
            id={`tab-${tab.key}`}
            onClick={() => { onChange(tab.key); }}
            onKeyDown={(e) => { handleKeyDown(e, idx); }}
            className={[
              'min-h-[44px] flex-none px-4 py-2 text-sm transition-colors',
              isActive
                ? 'text-ink-900 font-semibold border-b-2 border-brand-600 -mb-px'
                : 'text-ink-500 hover:text-brand-600',
            ].join(' ')}
          >
            {tab.label}
            {!tab.hideCount && (
              <>
                {' '}
                ({tab.tileCount})
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
